import React from 'react';
import {
  Box,
  Fab,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Skeleton,
  Tab,
  Typography,
} from '@mui/material';
import { gql, useQuery } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';
import fragments from 'services/fragments';
import { accountSelectedSelector } from 'atoms';
import { useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { PersonType, UserAndContact } from 'types/UsersAndContact.types';
import EmptyState from 'components/EmptyState';
import RequirePermission from 'components/RequirePermission';
import CommonList from './CommonList';
import { Add } from '@mui/icons-material';
import ContactFormModal from './ContactFormModal';
import ConfirmDeleteModal from 'components/ConfirmDeleteModal';
import useDeleteContact from './useDeleteContact';

const USERS_AND_CONTACTS = gql`
  ${fragments.UserAndContacts}
  query UsersAndContacts(
    $isCloud: Boolean!
    $accountId: Int!
    $masterCompanyId: Int!
    $webService: String
  ) {
    userAndContactByAccountId(
      isCloud: $isCloud
      accountId: $accountId
      masterCompanyId: $masterCompanyId
      webService: $webService
    ) {
      ...UserAndContactsProperties
    }
  }
`;

function UsersAndContactsPage() {
  const accountSelected = useRecoilValue(accountSelectedSelector);
  const [searchParams, setSearchParams] = useSearchParams();
  const currentTab = searchParams.get('currentTab') || 'contacts';
  const { t } = useTranslation('_common');

  const {
    data,
    error,
    loading,
    refetch: refetchContacts,
  } = useQuery<{
    userAndContactByAccountId: UserAndContact[];
  }>(USERS_AND_CONTACTS, {
    fetchPolicy: 'no-cache',
    variables: {
      ...accountSelected,
      accountId: accountSelected?.id || 0,
    },
  });

  const contacts = React.useMemo(
    () => data?.userAndContactByAccountId?.filter((person) => person.type !== PersonType.User),
    [data],
  );

  const users = React.useMemo(
    () => data?.userAndContactByAccountId?.filter((person) => person.type !== PersonType.Contact),
    [data],
  );

  if (error) {
    throw error;
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        height: '100%',
      }}
    >
      <TabContext value={currentTab}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <TabList
            onChange={(e, value) => setSearchParams({ currentTab: value })}
            aria-label="tabs contacts and users"
            variant="fullWidth"
          >
            <Tab
              id="contacts"
              label={t('contacts')}
              aria-controls="tabpanel-contacts"
              value="contacts"
            />
            <Tab id="users" label={t('users')} aria-controls="tabpanel-users" value="users" />
          </TabList>
        </Box>
        {loading && <LoadingList />}
        <TabPanel
          value="contacts"
          sx={{
            p: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            flex: currentTab === 'contacts' ? '1 1 auto' : undefined,
            position: 'relative',
          }}
        >
          {contacts && (
            <RequirePermission validatePermission={(account) => !!account?.showContacts}>
              <ContactsList {...{ contacts, refetchContacts }} />
            </RequirePermission>
          )}
        </TabPanel>
        <TabPanel
          value="users"
          sx={{
            p: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            flex: currentTab === 'users' ? '1 1 auto' : undefined,
          }}
        >
          {users && (
            <RequirePermission validatePermission={(account) => !!account?.showUsers}>
              <UsersList {...{ users }} />
            </RequirePermission>
          )}
        </TabPanel>
      </TabContext>
    </Paper>
  );
}

function LoadingList() {
  return (
    <List>
      {Array.from({ length: 5 }).map((_, i) => (
        <ListItem key={i}>
          <ListItemAvatar>
            <Skeleton variant="circular" width={40} height={40} />
          </ListItemAvatar>
          <ListItemText primary={<Skeleton />} secondary={<Skeleton />} />
        </ListItem>
      ))}
    </List>
  );
}

function ContactsList({
  contacts,
  refetchContacts,
}: {
  contacts: UserAndContact[];
  refetchContacts: () => void;
}) {
  const { t } = useTranslation(['_common', 'users_and_contacts']);
  const [openEdit, setOpenEdit] = React.useState<UserAndContact | boolean>(false);
  const [openDelete, setOpenDelete] = React.useState<UserAndContact | boolean>(false);
  const { handleDelete, loading: loadingDelete } = useDeleteContact({
    t,
    item: typeof openDelete === 'object' ? (openDelete as UserAndContact) : null,
    setOpenDelete,
    refetchContacts,
  });

  if (contacts.length === 0) {
    return (
      <EmptyState
        title={t('_common:there_is_still_nothing_here')}
        description={t('users_and_contacts:there_are_no_contacts_for_this_account')}
        sx={{ flex: '1 1 auto' }}
      />
    );
  }

  return (
    <>
      <CommonList
        items={contacts}
        mountItemName={(item) => {
          if (!item.functionName) {
            return item.name;
          }
          return `${item.name} - ${item.functionName}`;
        }}
        onEdit={(item) => setOpenEdit(item)}
        onDelete={(item) => setOpenDelete(item)}
      />
      <RequirePermission validatePermission={(account) => !!account?.hasContactEditionPermission}>
        <Fab
          color="primary"
          aria-label="add"
          onClick={() => setOpenEdit(true)}
          sx={{ position: 'absolute', bottom: '16px', right: '16px' }}
        >
          <Add />
        </Fab>
      </RequirePermission>
      <ContactFormModal
        {...{
          open: !!openEdit,
          setOpen: setOpenEdit,
          refetchContacts,
          initialItem: typeof openEdit === 'object' ? openEdit : null,
        }}
      />
      <ConfirmDeleteModal
        open={!!openDelete}
        setOpen={setOpenDelete}
        onConfirm={handleDelete}
        loading={loadingDelete}
      />
    </>
  );
}

function UsersList({ users }: { users: UserAndContact[] }) {
  const { t } = useTranslation(['_common', 'users_and_contacts']);
  if (users.length === 0) {
    return (
      <EmptyState
        title={t('_common:there_is_still_nothing_here')}
        description={t('users_and_contacts:there_are_no_users_for_this_account')}
        sx={{ flex: '1 1 auto' }}
      />
    );
  }
  return (
    <CommonList
      items={users}
      mountItemName={(item) =>
        item.codeAllPartitions ? (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mr: 0.5 }}
            >{`#${item.codeAllPartitions}`}</Typography>
            {item.name}
          </Box>
        ) : (
          item.name
        )
      }
    />
  );
}

export default UsersAndContactsPage;
